"use client"

import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSnackbar } from "notistack"
import { Link } from "react-router-dom"
import { MapPin, Phone, Mail, Facebook, Twitter, Instagram, Linkedin, ExternalLink, ChevronRight, ArrowRight, ChevronDown, Search } from "lucide-react"
import logo from "../assets/logo/logo.png"
import { Button } from "./ui/button"
import { subscribeNewsletter, clearError, clearSubscribeSuccess } from "../store/slices/newsletterSlice"
import { getCountries } from "../utils/countries"

const inputClass = "w-full bg-[#0a192f] border border-white/10 rounded-lg py-3 px-4 text-sm text-white focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all placeholder:text-slate-500"

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { loading, error, subscribeSuccess } = useSelector((s) => s.newsletter)
  const [form, setForm] = useState({ fullname: "", email: "", country: "" })
  const [countries, setCountries] = useState([])
  const [countryPopupOpen, setCountryPopupOpen] = useState(false)
  const [countrySearch, setCountrySearch] = useState("")
  const countryPopupRef = useRef(null)

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]))
  }, [])

  useEffect(() => {
    function handleClickOutside(e) {
      if (countryPopupRef.current && !countryPopupRef.current.contains(e.target)) {
        setCountryPopupOpen(false)
      }
    }
    if (countryPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [countryPopupOpen])

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  )

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" })
      dispatch(clearError())
    }
  }, [error, enqueueSnackbar, dispatch])

  useEffect(() => {
    if (subscribeSuccess) {
      enqueueSnackbar(subscribeSuccess?.message || "Inscription réussie.", { variant: "success" })
      dispatch(clearSubscribeSuccess())
      setForm({ fullname: "", email: "", country: "" })
    }
  }, [subscribeSuccess, enqueueSnackbar, dispatch])

  const handleSubmit = (e) => {
    e.preventDefault()
    dispatch(subscribeNewsletter({ ...form, source: "website" }))
  }

  const footerLinks = {
    academics: {
      title: "Nos Facultés",
      links: [
        { name: "Droit, Économie & Gestion (FDSEG)", href: "/faculte/fdseg" },
        { name: "Lettres, Langues & Arts (FLLCA)", href: "/faculte/fllca" },
        { name: "Médecine & Sciences de la Santé", href: "/faculte/medecine" },
        { name: "Sciences Agronomiques (SAV)", href: "/faculte/sav" },
        { name: "Sciences & Technologies", href: "/faculte/sciences" },
        { name: "Masters FSAV (Nutrition, Développement rural, Agribusiness)", href: "/masters/nutrition" },
        { name: "Masters FDSEG (Droit, Économie, Gestion)", href: "/masters/droit-public" }
      ]
    },
    admissions: {
      title: "Admissions",
      links: [
        { name: "Comment s'inscrire", href: "/admission" },
        { name: "Calendrier académique", href: "/etudiants/calendrier" },
        { name: "Vie sur le campus", href: "/campus" }
      ]
    },
    resources: {
      title: "Ressources",
      links: [
        { name: "Blog & Espace Étudiant", href: "/etudiants/blog" },
        { name: "Alumni", href: "/alumni/events" },
        { name: "Opportunités carrière", href: "/alumni/events" },
        { name: "Partenariats & Conventions", href: "/partenariats" },
        { name: "Recherche & Publications", href: "/recherche/publications" },
        { name: "Actualités", href: "/actualites" }
      ]
    }
  }

  return (
    <footer className="relative bg-[#0a192f] text-white overflow-hidden border-t border-white/5">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent via-primary to-accent opacity-20" />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 mb-16">

          {/* Brand Column (3 cols) */}
          <div className="lg:col-span-3 space-y-8">
            <div className="flex items-center gap-4 group cursor-default">
              <div className="w-14 h-14 bg-white border border-white/10 flex items-center justify-center rounded-xl overflow-hidden p-1 transition-all duration-500 group-hover:border-accent/50 group-hover:scale-105">
                <img
                  src={logo}
                  alt="Logo UNG"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="text-lg font-bold font-serif tracking-tight text-white leading-tight">Université de Ngozi</div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-accent font-bold mt-1">Paix • Savoir • Développement</div>
              </div>
            </div>

            <p className="text-slate-300 text-sm leading-relaxed">
              Excellence académique, recherche innovante et engagement communautaire depuis 1999. Former les leaders qui transforment l'Afrique.
            </p>

            <div className="flex gap-3">
              {[
                { icon: Facebook, label: "Facebook", url: "https://www.facebook.com/universite.ngozi" },
                { icon: Twitter, label: "Twitter", url: "https://twitter.com/UNG_Burundi" },
                { icon: Linkedin, label: "LinkedIn", url: "https://www.linkedin.com/company/universite-ngozi/" },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-slate-300 hover:text-white hover:bg-accent hover:border-accent transition-all duration-300 group"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                </a>
              ))}
            </div>

            <div className="space-y-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors cursor-pointer">
                <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                <span>B.P.137 NGOZI, Burundi</span>
              </div>
              <a href="tel:+25722302259" className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-accent flex-shrink-0" />
                <span>+257 22 30 22 59</span>
              </a>
              <a href="mailto:uningozi@univ-ngozi.bi" className="flex items-center gap-3 text-sm text-slate-300 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-accent flex-shrink-0" />
                <span>uningozi@univ-ngozi.bi</span>
              </a>
            </div>
          </div>

          {/* Links Columns (6 cols total) */}
          <div className="lg:col-span-6 grid sm:grid-cols-3 gap-8">
            {Object.entries(footerLinks).map(([key, section]) => (
              <div key={key}>
                <h3 className="font-serif text-lg text-white mb-6 flex items-center gap-2">
                  <span className="w-1 h-4 bg-accent rounded-sm" />
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm text-slate-300 hover:text-white transition-all duration-200 flex items-center gap-1 group"
                      >
                        <ChevronRight className="w-3 h-3 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200 text-accent" />
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter Column (3 cols) - Backend requires fullname, email, country */}
          <div id="newsletter" className="lg:col-span-3 scroll-mt-24">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <h3 className="font-serif text-lg text-white mb-2">Restez informé</h3>
              <p className="text-sm text-slate-300 mb-6">
                Recevez les dernières actualités, événements et opportunités académiques directement dans votre boîte mail.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  required
                  placeholder="Nom complet"
                  value={form.fullname}
                  onChange={(e) => setForm((f) => ({ ...f, fullname: e.target.value }))}
                  className={inputClass}
                />
                <input
                  type="email"
                  required
                  placeholder="Votre adresse email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputClass}
                />
                {/* Country dropdown — custom popup to match site design */}
                <div className="relative" ref={countryPopupRef}>
                  <button
                    type="button"
                    onClick={() => setCountryPopupOpen((o) => !o)}
                    className={`${inputClass} w-full flex items-center justify-between gap-2 cursor-pointer text-left ${
                      !form.country ? "text-slate-500" : "text-white"
                    }`}
                    aria-haspopup="listbox"
                    aria-expanded={countryPopupOpen}
                    aria-label="Choisir un pays"
                  >
                    <span className="truncate">{form.country || "Sélectionner un pays"}</span>
                    <ChevronDown
                      className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform duration-200 ${
                        countryPopupOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <input
                    type="hidden"
                    name="country"
                    value={form.country}
                    required
                    readOnly
                    aria-hidden
                  />
                  {countryPopupOpen && (
                    <div
                      className="absolute z-50 left-0 right-0 mt-1 rounded-xl border border-white/15 bg-[#0d2137] shadow-xl shadow-black/30 overflow-hidden"
                      role="listbox"
                    >
                      <div className="p-2 border-b border-white/10">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            type="text"
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            placeholder="Rechercher un pays..."
                            className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-56 overflow-y-auto overscroll-contain">
                        {filteredCountries.length === 0 ? (
                          <p className="py-4 px-4 text-sm text-slate-400 text-center">Aucun pays trouvé</p>
                        ) : (
                          filteredCountries.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              role="option"
                              aria-selected={form.country === c.name}
                              onClick={() => {
                                setForm((f) => ({ ...f, country: c.name }))
                                setCountryPopupOpen(false)
                                setCountrySearch("")
                              }}
                              className={`w-full text-left py-2.5 px-4 text-sm transition-colors ${
                                form.country === c.name
                                  ? "bg-accent/20 text-white font-medium"
                                  : "text-slate-300 hover:bg-white/10 hover:text-white"
                              }`}
                            >
                              {c.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold group disabled:opacity-60"
                >
                  {loading ? "Envoi..." : "S'abonner"}
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>

              <p className="text-xs text-slate-400 mt-4 leading-normal">
                En vous inscrivant, vous acceptez notre politique de confidentialité. Désinscription possible à tout moment.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-sm text-slate-400 order-2 md:order-1">
            © {currentYear} Université de Ngozi. Tous droits réservés.
          </p>

          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 order-1 md:order-2">
            <div className="flex gap-6 text-xs uppercase tracking-wider font-semibold text-slate-300">
              <Link to="/" className="hover:text-accent hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/" className="hover:text-accent hover:text-white transition-colors">Confidentialité</Link>
              <Link to="/" className="hover:text-accent hover:text-white transition-colors">Plan du site</Link>
            </div>

            <div className="h-4 w-px bg-white/10 hidden md:block" />

            <a
              href="https://hyphentech.africa"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors group bg-white/10 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/20"
            >
              <span>Développé par</span>
              <span className="font-bold text-white/90 group-hover:text-accent transition-colors">Hyphen Tech</span>
              <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
